import React from "react";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import { useAuth0 } from "@auth0/auth0-react";
import { getPolicyDownloadSignedUrl } from "../../../../apis/settingsApis/policy-apis/policyApis";
import ActionsMenu from "../../../../components/ActionsMenu";
import { FEATURE_IDS } from "../../../../components/AccessControl";

const PolicyDetailsActionsMenu = ({ policy, onEditDocument, onDownloadTemplate, onDownloadError, onRemovePolicy }) => {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  const isCustomPolicy = policy?.policyName?.includes?.("custom_policy");

  const isDocxPolicy =
    policy?.filePath?.toLowerCase().endsWith(".docx") ||
    policy?.fileName?.toLowerCase().endsWith(".docx");

  const getDownloadFileName = (format) => {
    const name = policy?.policyDisplayName || "policy.docx";
    const base = name.includes("/") ? name.split("/").pop() : name;
    if (format === "pdf") return base.replace(/\.docx$/i, ".pdf");
    return base;
  };

  const handleDownloadDocument = async (format) => {
    const link = document.createElement("a");
    const isPdf = format === "pdf" && isDocxPolicy;
    const filePath = isPdf
      ? policy?.filePath?.replace(/\.docx$/i, ".pdf")
      : policy?.filePath;
    const downloadFileName = isPdf ? getDownloadFileName("pdf") : getDownloadFileName();

    try {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims())?.__raw;
      const { url: signedUrl } = await getPolicyDownloadSignedUrl(
        filePath,
        downloadFileName,
        accessToken,
        idToken
      );

      // Fetch as blob and trigger download via object URL so the browser always downloads
      // (cross-origin signed URLs ignore the <a download> attribute).
      try {
        const res = await fetch(signedUrl, { mode: "cors" });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = downloadFileName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
      } catch {
        // CORS or fetch failed: fallback to opening the URL (may open in tab or download depending on server headers)
        link.href = signedUrl;
        link.download = downloadFileName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      onDownloadError?.("Download failed. Please try again.");
    }
  };

  const menuItems = [
    { label: "Upload New Version", onClick: () => onEditDocument?.() },
    ...(!isCustomPolicy
      ? [{ label: "Download Template", onClick: () => onDownloadTemplate?.() }]
      : []),
    ...(isCustomPolicy && onRemovePolicy
      ? [{ label: "Delete policy", onClick: () => onRemovePolicy?.() }]
      : []),
    {
      label: "Download Policy",
      featureId: FEATURE_IDS.POLICY_DETAIL_DOWNLOAD_POLICY,
      onClick: !isDocxPolicy ? () => handleDownloadDocument() : undefined,
      children: isDocxPolicy
        ? [
          { label: "Download as PDF", onClick: () => handleDownloadDocument("pdf") },
          { label: "Download as DOCX", onClick: () => handleDownloadDocument("docx") },
        ]
        : [],
    }
  ];

  return (
    <ActionsMenu
      ariaLabel="Policy actions"
      items={menuItems}
    />
  );
};

export default PolicyDetailsActionsMenu;
